import React, { useState } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Switch } from "@/components/ui/switch";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Progress } from "@/components/ui/progress";
import { 
  Users, 
  Database, 
  RefreshCw, 
  Settings, 
  CheckCircle, 
  AlertTriangle,
  Clock,
  FileText,
  Download,
  Upload
} from "lucide-react";
import { toast } from "sonner";

interface SISConfig {
  name: string;
  enabled: boolean;
  connectionString: string;
  syncInterval: string;
  lastSync: string;
  totalUsers: number;
  syncStatus: 'idle' | 'syncing' | 'success' | 'error';
}

export default function SISIntegration() {
  const [sisConfigs, setSisConfigs] = useState<Record<string, SISConfig>>({
    synergy: {
      name: 'Synergy SIS',
      enabled: true,
      connectionString: 'Server=syn-db.district.edu;Database=SynergyEDU;Integrated Security=true',
      syncInterval: '24',
      lastSync: '2024-01-15 02:00:00',
      totalUsers: 1247,
      syncStatus: 'success'
    },
    ivisions: {
      name: 'iVisions Portal',
      enabled: true,
      connectionString: 'https://ivisions.district.edu/api/v2',
      syncInterval: '12',
      lastSync: '2024-01-15 14:00:00',
      totalUsers: 892,
      syncStatus: 'idle'
    }
  });

  const [syncProgress, setSyncProgress] = useState(0);
  const [fieldMappings, setFieldMappings] = useState({
    synergy: {
      employeeId: 'PersonnelMaster.PersonnelMasterGU',
      firstName: 'PersonnelMaster.FirstName',
      lastName: 'PersonnelMaster.LastName',
      email: 'PersonnelMaster.Email',
      department: 'PersonnelMaster.Department',
      position: 'PersonnelMaster.PositionTitle',
      hireDate: 'PersonnelMaster.HireDate'
    },
    ivisions: {
      employeeId: 'employee.id',
      firstName: 'employee.first_name',
      lastName: 'employee.last_name',
      email: 'employee.email_address',
      department: 'employee.department_name',
      position: 'employee.job_title',
      hireDate: 'employee.start_date'
    }
  });

  const handleSyncNow = async (sisKey: string) => {
    setSisConfigs(prev => ({
      ...prev,
      [sisKey]: { ...prev[sisKey], syncStatus: 'syncing' }
    }));

    setSyncProgress(0);
    const interval = setInterval(() => {
      setSyncProgress(prev => {
        if (prev >= 100) {
          clearInterval(interval);
          setSisConfigs(current => ({
            ...current,
            [sisKey]: { 
              ...current[sisKey], 
              syncStatus: 'success',
              lastSync: new Date().toLocaleString()
            }
          }));
          toast.success(`${sisConfigs[sisKey].name} sync completed successfully`);
          return 100;
        }
        return prev + 10;
      });
    }, 200);
  };

  const updateSISConfig = (sisKey: string, updates: Partial<SISConfig>) => {
    setSisConfigs(prev => ({
      ...prev,
      [sisKey]: { ...prev[sisKey], ...updates }
    }));
  };

  const testConnection = async (sisKey: string) => {
    toast.info(`Testing connection to ${sisConfigs[sisKey].name}...`);
    setTimeout(() => {
      toast.success(`Successfully connected to ${sisConfigs[sisKey].name}`);
    }, 2000);
  };

  return (
    <div className="container mx-auto p-6 space-y-6">
      <div>
        <h1 className="text-3xl font-bold text-foreground">SIS Integration</h1>
        <p className="text-muted-foreground">Configure Student Information System integrations for automated user management</p>
      </div>

      <Tabs defaultValue="systems" className="space-y-6">
        <TabsList className="grid w-full grid-cols-4">
          <TabsTrigger value="systems">SIS Systems</TabsTrigger>
          <TabsTrigger value="field-mapping">Field Mapping</TabsTrigger>
          <TabsTrigger value="sync-logs">Sync Logs</TabsTrigger>
          <TabsTrigger value="bulk-operations">Bulk Operations</TabsTrigger>
        </TabsList>

        <TabsContent value="systems" className="space-y-6">
          <div className="grid gap-6">
            {Object.entries(sisConfigs).map(([key, config]) => (
              <Card key={key}>
                <CardHeader>
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <Database className="h-5 w-5" />
                      <CardTitle>{config.name}</CardTitle>
                      <Badge variant={
                        config.syncStatus === 'success' ? 'secondary' :
                        config.syncStatus === 'syncing' ? 'default' :
                        config.syncStatus === 'error' ? 'destructive' : 'outline'
                      }>
                        {config.syncStatus === 'syncing' ? 'Syncing' : 
                         config.syncStatus === 'success' ? 'Connected' :
                         config.syncStatus === 'error' ? 'Error' : 'Idle'}
                      </Badge>
                    </div>
                    <Switch
                      checked={config.enabled}
                      onCheckedChange={(enabled) => updateSISConfig(key, { enabled })}
                    />
                  </div>
                  <CardDescription>
                    Last sync: {config.lastSync} • {config.totalUsers.toLocaleString()} users
                  </CardDescription>
                </CardHeader>
                {config.enabled && (
                  <CardContent className="space-y-4">
                    <div>
                      <Label htmlFor={`${key}-connection`}>Connection String</Label>
                      <Input
                        id={`${key}-connection`}
                        value={config.connectionString}
                        onChange={(e) => updateSISConfig(key, { connectionString: e.target.value })}
                        placeholder="Enter connection string or API endpoint"
                        type="password"
                      />
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <Label htmlFor={`${key}-interval`}>Sync Interval (hours)</Label>
                        <Select
                          value={config.syncInterval}
                          onValueChange={(value) => updateSISConfig(key, { syncInterval: value })}
                        >
                          <SelectTrigger>
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="1">Every Hour</SelectItem>
                            <SelectItem value="6">Every 6 Hours</SelectItem>
                            <SelectItem value="12">Every 12 Hours</SelectItem>
                            <SelectItem value="24">Daily</SelectItem>
                            <SelectItem value="168">Weekly</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                      <div className="flex items-end">
                        <div className="grid grid-cols-2 gap-2 w-full">
                          <Button variant="outline" onClick={() => testConnection(key)}>
                            Test Connection
                          </Button>
                          <Button 
                            onClick={() => handleSyncNow(key)}
                            disabled={config.syncStatus === 'syncing'}
                          >
                            {config.syncStatus === 'syncing' ? 'Syncing...' : 'Sync Now'}
                          </Button>
                        </div>
                      </div>
                    </div>

                    {config.syncStatus === 'syncing' && (
                      <div className="space-y-2">
                        <div className="flex items-center justify-between text-sm">
                          <span>Sync Progress</span>
                          <span>{syncProgress}%</span>
                        </div>
                        <Progress value={syncProgress} className="w-full" />
                      </div>
                    )}

                    <Alert>
                      <CheckCircle className="h-4 w-4" />
                      <AlertDescription>
                        <strong>Sync Features:</strong> Automatic user provisioning, department mapping, role assignment based on position data, and duplicate detection.
                      </AlertDescription>
                    </Alert>
                  </CardContent>
                )}
              </Card>
            ))}
          </div>
        </TabsContent>

        <TabsContent value="field-mapping" className="space-y-6">
          {Object.entries(fieldMappings).map(([sisKey, mappings]) => (
            <Card key={sisKey}>
              <CardHeader>
                <CardTitle className="capitalize">{sisKey} Field Mapping</CardTitle>
                <CardDescription>
                  Map {sisConfigs[sisKey].name} fields to system user attributes
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {Object.entries(mappings).map(([fieldKey, fieldValue]) => (
                    <div key={fieldKey}>
                      <Label htmlFor={`${sisKey}-${fieldKey}`} className="capitalize">
                        {fieldKey.replace(/([A-Z])/g, ' $1').trim()}
                      </Label>
                      <Input
                        id={`${sisKey}-${fieldKey}`}
                        value={fieldValue}
                        onChange={(e) => setFieldMappings(prev => ({
                          ...prev,
                          [sisKey]: { ...prev[sisKey], [fieldKey]: e.target.value }
                        }))}
                        placeholder={`${sisKey}.${fieldKey}`}
                      />
                    </div>
                  ))}
                </div>
                <Button>Save Field Mappings</Button>
              </CardContent>
            </Card>
          ))}
        </TabsContent>

        <TabsContent value="sync-logs" className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <FileText className="h-5 w-5" />
                Synchronization Logs
              </CardTitle>
              <CardDescription>
                Monitor SIS synchronization activity and troubleshoot issues
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                {[
                  { 
                    time: '2024-01-15 14:30:00', 
                    system: 'iVisions', 
                    action: 'Full Sync Completed', 
                    status: 'success',
                    details: '892 users processed, 15 new, 8 updated'
                  },
                  { 
                    time: '2024-01-15 02:15:00', 
                    system: 'Synergy', 
                    action: 'Incremental Sync', 
                    status: 'success',
                    details: '1,247 users processed, 3 new, 12 updated'
                  },
                  { 
                    time: '2024-01-14 14:22:00', 
                    system: 'iVisions', 
                    action: 'Connection Test', 
                    status: 'warning',
                    details: 'Slow response time detected (5.2s)'
                  },
                  { 
                    time: '2024-01-14 08:45:00', 
                    system: 'Synergy', 
                    action: 'Full Sync Failed', 
                    status: 'error',
                    details: 'Database connection timeout after 30s'
                  }
                ].map((log, index) => (
                  <div key={index} className="flex items-center justify-between p-3 border rounded">
                    <div className="flex items-center gap-3">
                      {log.status === 'success' && <CheckCircle className="h-4 w-4 text-success" />}
                      {log.status === 'warning' && <AlertTriangle className="h-4 w-4 text-warning" />}
                      {log.status === 'error' && <AlertTriangle className="h-4 w-4 text-destructive" />}
                      <div>
                        <div className="flex items-center gap-2">
                          <p className="text-sm font-medium">{log.action}</p>
                          <Badge variant="outline" className="text-xs">{log.system}</Badge>
                        </div>
                        <p className="text-xs text-muted-foreground">{log.details}</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-2 text-xs text-muted-foreground">
                      <Clock className="h-3 w-3" />
                      {log.time}
                    </div>
                  </div>
                ))}
              </div>

              <div className="flex justify-between items-center mt-4 pt-4 border-t">
                <Button variant="outline" size="sm">
                  <Download className="h-4 w-4 mr-2" />
                  Export Logs
                </Button>
                <Button variant="outline" size="sm">
                  <RefreshCw className="h-4 w-4 mr-2" />
                  Refresh
                </Button>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="bulk-operations" className="space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Upload className="h-5 w-5" />
                  Bulk User Import
                </CardTitle>
                <CardDescription>
                  Import users from CSV or Excel files
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="border-2 border-dashed border-border rounded-lg p-6 text-center">
                  <Upload className="h-8 w-8 mx-auto mb-2 text-muted-foreground" />
                  <p className="text-sm text-muted-foreground mb-2">
                    Drop your CSV or Excel file here, or click to browse
                  </p>
                  <Button variant="outline" size="sm">
                    Choose File
                  </Button>
                </div>
                <Alert>
                  <AlertTriangle className="h-4 w-4" />
                  <AlertDescription>
                    Ensure your file includes required fields: Employee ID, First Name, Last Name, Email, Department
                  </AlertDescription>
                </Alert>
                <Button className="w-full" disabled>
                  Process Import
                </Button>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Download className="h-5 w-5" />
                  Bulk User Export
                </CardTitle>
                <CardDescription>
                  Export user data for external processing
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div>
                  <Label>Export Format</Label>
                  <Select defaultValue="csv">
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="csv">CSV</SelectItem>
                      <SelectItem value="xlsx">Excel (XLSX)</SelectItem>
                      <SelectItem value="json">JSON</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Include Fields</Label>
                  <div className="grid grid-cols-2 gap-2 mt-2">
                    {['Employee ID', 'Name', 'Email', 'Department', 'Position', 'Hire Date'].map((field) => (
                      <div key={field} className="flex items-center space-x-2">
                        <input type="checkbox" defaultChecked className="rounded" />
                        <Label className="text-sm">{field}</Label>
                      </div>
                    ))}
                  </div>
                </div>
                <Button className="w-full">
                  Generate Export
                </Button>
              </CardContent>
            </Card>
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
}